import type { TFunction } from "i18next";
import { useState, useRef, useEffect } from "react";
import type { AxiosInstance } from "axios";
import type { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { U2000FormStateEnumEditMode } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { useU2000LUStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000LUHandlers";
import { U2000LUStateEnumNextAction } from "../../../../../u2000Core/reducer/formReducer/U2000LUState";
import type { NavigateFunction } from "react-router-dom";
import type { U3148Role } from "../../../../U3148Service";

// Gestion des handlers pour les actions d'une unité logique
export interface U3148OrganizationrRoleHandler {
  onRowClick: (role: U3148Role) => void;
  onAdd: () => void;
  onModify: () => void;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3148Role>>;
  setDataChangedEvent: (dataChangedEvent: number) => void;
  dataChangedEvent: number;
}

// Paramètres pour les handlers des actions d'une unité logique
export interface U3148OrganizationRoleHandlerParam {
  u2000AxiosAuthenticated: AxiosInstance;
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3148Role>>;
  t: TFunction;
  organizationId: string;
  navigate: NavigateFunction;
}

// Gestion des handlers pour les actions d'une unité logique
export interface U3148OrganizationRoleHandlerReturn {
  onRowClick: (role: U3148Role) => void;
  onAdd: () => void;
  onModify: () => void;

  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3148Role>>;
  setDataChangedEvent: (dataChangedEvent: number) => void;
  dataChangedEvent: number;
}

// Implémentation des handlers pour les actions d'une unité logique
export function u3148OrganizationRoleHandler(params: U3148OrganizationRoleHandlerParam): U3148OrganizationRoleHandlerReturn {
  const { luStateHandlers, gridStateHandlers, navigate } = params;
  const [dataChangedEvent, setDataChangedEvent] = useState<number>(0);
  const [localEditMode, _setLocalEditMode] = useState<U2000FormStateEnumEditMode>(U2000FormStateEnumEditMode.NONE);
  const localEditModeRef = useRef(localEditMode);

  useEffect(() => {
    localEditModeRef.current = localEditMode;
  }, [localEditMode]);

  // Gestion du clic sur une ligne du tableau des rôles
  const onRowClick = (role: U3148Role) => {
    luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
    //luStateHandlers.setActionOriginFrom(`/U3148/${organizationId}`);
    luStateHandlers.setActionOriginFrom(`/U3148/ROLE`);
    const currentIdx = gridStateHandlers ? gridStateHandlers.getData().findIndex((r) => r.id === role.id) : -1;
    if (gridStateHandlers) {
      gridStateHandlers.setCurrentIdx(currentIdx);
    }
    navigate(`role/${role.id}`);
  };

  // Gestion de l'ajout d'une action d'unité logique
  const onAdd = (): void => {
    luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
    //luStateHandlers.setActionOriginFrom(`/U3148/${organizationId}`);
    luStateHandlers.setActionOriginFrom(`/U3148/ORGANIZATION`);
    navigate("role/new");
  };

  // Gestion de la modification d'une action d'unité logique
  const onModify = (): void => {
    luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
    //luStateHandlers.setActionOriginFrom(`/U3148/${organizationId}`);
    luStateHandlers.setActionOriginFrom(`/U3148/ORGANIZATION`);
    navigate(`role/${gridStateHandlers.getSelectedRows()[0]?.id}`);
  };

  return {
    onRowClick,
    onAdd,
    onModify,

    gridStateHandlers,
    setDataChangedEvent,
    dataChangedEvent,
  };
}
